import { useMemo, useState } from 'react';
import { useLocalization } from '@/hooks/use-localization';
import { validators } from '@/hooks/use-validation';
import { Select } from '@/isomorphic/components/forms/components/select';
import { Field } from '@/isomorphic/components/forms/components/field';
import { Button } from '@/isomorphic/components/typography/button/button';
import { FieldGroup } from '../field-group';
import { FieldValuesType, PropsType } from '../../types';
import { StepLayout } from './step-layout';
import { useFieldValidation } from '../../hooks/use-field-validation';
import { Kyc } from '../kyc';
import { ExplainerModal } from '../explainer-modal';
import { getImageUrl } from '@/utils/images';
import { AccountClearingNumber } from '../../../bank-open-savings-form/components/account-clearing-number';

export const StepOne = ({ kyc, getLabel, updateFields, setManualApplicationOnly, fields, scrollToTop, ...rest }: PropsType) => {
    const { company, payoutAccount, applicant } = fields;
    const { getLanguage, getLocale } = useLocalization();
    const { validateObject, allFieldsValid } = useFieldValidation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const validationState = useMemo(() => {
        const requiredFields = {
            company: validateObject(fields.company),
            applicant: validateObject(fields.applicant),
            payoutAccount: validateObject(fields.payoutAccount),
            companyKyc: kyc.getValidationObjectForGroup('COMPANY'),
            kyc: kyc.getValidationObjectForGroup('CORPORATE_SAVINGS'),
        };

        return {
            ...requiredFields,
            allFieldsValid: Object.keys(requiredFields).every(key => allFieldsValid(requiredFields[key as keyof typeof requiredFields])),
        };
    }, [fields, kyc]);

    const companyTypeSelectOptions = useMemo(() => {
        const group = kyc.kycSurveyData?.groups?.find(group => group.type === 'COMPANY');
        const companyTypes = group?.questions
            ?.find(question => question.id === 103)
            ?.options?.map(({ text: label }) => ({
                value: label,
                label,
            }));

        return companyTypes ? [{ value: '', label: `-- ${getLabel('select')} --` }, ...companyTypes] : [];
    }, [kyc]);

    const updateCompanyAddress = (field: Partial<FieldValuesType['company']['address']>) =>
        updateCompany({
            address: {
                ...company.address,
                ...field,
            },
        });

    const updateApplicant = (field: Partial<FieldValuesType['applicant']>) =>
        updateFields({
            applicant: {
                ...applicant,
                ...field,
            },
        });

    const updateName = (value: string, key: 'first' | 'last' = 'first') =>
        updateApplicant({
            name: {
                ...applicant.name,
                [key]: value,
            },
        });

    const isAssociationType = (value: string) => {
        const companyGroup = kyc.kycSurveyData?.groups.find(group => group.type === 'COMPANY');
        const companyTypes = companyGroup?.questions?.find(question => question.id === 103);
        const option = companyTypes?.options?.find(option => option.text === value);

        return option?.id === 5;
    };

    const updateCompany = (field: Partial<FieldValuesType['company']>) => {
        const companyFields = { ...company, ...field };
        updateFields({ company: companyFields });

        // We need to update the KYC values manually for the company as the data is
        // essentially duplicated in KYC that we need for the application flow data.
        const groupType = 'COMPANY';
        const address = [companyFields.address.street, `${companyFields.address.zipCode} ${companyFields.address.city}`].filter(v => v).join(', ');

        const questions = {
            ...kyc.kycValues[groupType].questions,
        };
        questions[102].answer = [address];
        questions[101].answer = [companyFields.governmentId.id];
        questions[100].answer = [companyFields.organizationName];
        questions[103].answer = [companyFields.organizationType];

        kyc.setKycValues(prev => ({
            ...prev,
            [groupType]: {
                ...prev[groupType],
                questions,
            },
        }));

        // Associations (föreningar) can only apply manually
        if (isAssociationType(company.organizationName)) {
            rest.setStep(0);
            setManualApplicationOnly(true);
        }
    };

    const isFieldDisabled = (key: keyof FieldValuesType['company']) => {
        const GROUP_TYPE = 'COMPANY';

        switch (key) {
            case 'organizationName':
                return !!kyc.getValue(100, GROUP_TYPE);
            case 'address':
                return !!kyc.getValue(102, GROUP_TYPE);
            case 'organizationType':
                return !!kyc.getValue(103, GROUP_TYPE);
        }

        return false;
    };

    const onSubmit = () => {
        if (validationState.allFieldsValid) {
            rest.setStep(2);
            return;
        }

        scrollToTop();
        setShowValidationErrors(true);
    };

    return (
        <>
            <StepLayout
                fields={fields}
                getLabel={getLabel}
                onSubmit={onSubmit}
                title={getLabel('companyInformation')}
                isSubmitAriaDisabled={!validationState.allFieldsValid}
                {...rest}
            >
                <div className="r-space-y-4">
                    <Select
                        label={getLabel('companyForm')}
                        value={company.organizationType}
                        options={companyTypeSelectOptions}
                        disabled={isFieldDisabled('organizationType')}
                        onChange={value => {
                            if (isAssociationType(`${value}`)) {
                                rest.setStep(0);
                                setManualApplicationOnly(true);
                            }
                            updateCompany({ organizationType: `${value}` });
                        }}
                        showRequiredError={showValidationErrors && !company.organizationType}
                    />
                    <FieldGroup>
                        <Field
                            grouped="top"
                            validation={v => !!v}
                            label={getLabel('address')}
                            value={company.address.street}
                            onChange={value => updateCompanyAddress({ street: value })}
                            showRequiredError={showValidationErrors && !validationState.company.address.street}
                        />
                        <Field
                            grouped="middle"
                            label={getLabel('postCode')}
                            value={company.address.zipCode}
                            validation={v => validators.postcode(v, getLanguage())}
                            onChange={value => updateCompanyAddress({ zipCode: value })}
                            showRequiredError={showValidationErrors && !validationState.company.address.zipCode}
                        />
                        <Field
                            grouped="middle"
                            validation={v => !!v}
                            label={getLabel('city')}
                            value={company.address.city}
                            onChange={value => updateCompanyAddress({ city: value })}
                            showRequiredError={showValidationErrors && !validationState.company.address.city}
                        />
                        <Field
                            grouped="middle"
                            value={company.email}
                            label={getLabel('email')}
                            validation={validators.email}
                            onChange={value => updateCompany({ email: value })}
                            showRequiredError={showValidationErrors && !validationState.company.email}
                        />
                        <Field
                            grouped="bottom"
                            label={getLabel('phone')}
                            value={company.phoneNumber}
                            validation={validators.swedishPhone}
                            onChange={value => updateCompany({ phoneNumber: value })}
                            showRequiredError={showValidationErrors && !validationState.company.phoneNumber}
                        />
                    </FieldGroup>

                    <div className="r-space-y-2 r-pb-2 r-pt-4">
                        <h4 className="r-text-2xl r-font-semibold">{getLabel('customerKnowledge')}</h4>
                        <Button type="link" onClick={() => setIsModalOpen(true)}>
                            {getLabel('aboutCustomerKnowledge')}
                        </Button>
                    </div>

                    <Kyc kyc={kyc} getLabel={getLabel} type="CORPORATE_SAVINGS" showValidationErrors={showValidationErrors} />
                    <div className="r-pt-4">
                        <Kyc kyc={kyc} getLabel={getLabel} type="COMPANY" showValidationErrors={showValidationErrors} />
                    </div>

                    <div className="r-space-y-2 r-pb-2 r-pt-4">
                        <h4 className="r-text-2xl r-font-semibold">{getLabel('paymentAccount')}</h4>
                        <p>{getLabel('paymentAccountInfo')}</p>
                    </div>
                    <FieldGroup>
                        <AccountClearingNumber
                            values={{
                                ...payoutAccount,
                                bankName: payoutAccount.accountHolderBank,
                            }}
                            labels={{
                                clearingNumber: getLabel('clearingNumber'),
                                accountNumber: getLabel('accountNumber'),
                                bankName: getLabel('bankName'),
                            }}
                            showRequiredError={showValidationErrors}
                            setValues={({ clearingNumber, accountNumber, bankName: accountHolderBank }) =>
                                updateFields({
                                    payoutAccount: {
                                        clearingNumber,
                                        accountNumber,
                                        accountHolderBank,
                                    },
                                })
                            }
                            accountNumberValidatedCallback={() => null}
                        />
                    </FieldGroup>
                </div>
                <h4 className="r-pb-4 r-pt-8 r-text-2xl r-font-semibold">{getLabel('contactDetails')}</h4>
                <FieldGroup>
                    <Field
                        grouped="top"
                        validation={v => !!v}
                        value={applicant.name.first}
                        label={getLabel('firstName')}
                        onChange={value => updateName(value)}
                        showRequiredError={showValidationErrors && !validationState.applicant.name.first}
                    />
                    <Field
                        grouped="middle"
                        validation={v => !!v}
                        value={applicant.name.last}
                        label={getLabel('lastName')}
                        onChange={value => updateName(value, 'last')}
                        showRequiredError={showValidationErrors && !validationState.applicant.name.last}
                    />
                    <Field
                        grouped="middle"
                        value={applicant.email}
                        label={getLabel('yourEmail')}
                        validation={validators.email}
                        onChange={value => updateApplicant({ email: value })}
                        showRequiredError={showValidationErrors && !validationState.applicant.email}
                    />
                    <Field
                        grouped="bottom"
                        label={getLabel('yourPhone')}
                        value={applicant.phoneNumber}
                        validation={value => validators.phoneMobile(value, getLocale())}
                        onChange={value => updateApplicant({ phoneNumber: value })}
                        showRequiredError={showValidationErrors && !validationState.applicant.phoneNumber}
                    />
                </FieldGroup>
            </StepLayout>

            <ExplainerModal
                open={isModalOpen}
                className="r-max-w-xl"
                onClose={() => setIsModalOpen(false)}
                header={getLabel('customerKnowledgeKYC')}
                imageSrc={getImageUrl('/inlaning/woman.jpg', '500xAUTO')}
                imageSrcMobile={getImageUrl('/inlaning/woman-mobile.jpg', '500xAUTO')}
            >
                <div dangerouslySetInnerHTML={{ __html: getLabel('customerKnowledgeModalExplainer') }} />
            </ExplainerModal>
        </>
    );
};
